import Grid from "../Abstract/Grid/Grid";
import { HBox, PuzzleButton, PuzzleCheckbox, withDynamicSize, WithTooltip } from "puzzle";
import gridFilter from "../Abstract/GridFilter";
import { GridCellProps } from "@progress/kendo-react-grid";
import React, { ReactElement } from "react";
import {
  commonLocalizationGetter, disableActions,
  getTableTdAccessibilityProps, isDateExpired,
  isSubmissionDueSoon,
  SUBMISSION_STATUSES_MAP_SERVER_CORE_UI
} from "../util/Util";
import classNames from "classnames";
import GridProgressBar from "../util/GridProgressBar";
import WordCountLink from "../util/WordCountLink/WordCountLink";
import Policies from "../util/Policies";
import { isUndefined } from "lodash";
import { Notification, NotificationGroup } from "@progress/kendo-react-notification";

class MainGrid extends Grid <{}, {}> {
  static defaultProps = {
    ...Grid.defaultProps,
    nonReordeableColumns: ['checkboxSelection', 'statusColor', 'isFavorite', 'menuAction'],
    nonResizableColumns: ['checkboxSelection', 'statusColor', 'isFavorite', 'fileProgress', 'menuAction', 'instructions', 'notes'],
    defaultSort: [{
      field: 'submissionId',
      dir: 'desc'
    }],
    // Will be overwritten since it is being passed from the Dashboard
    actionsNames: {
      filter: 'filterMainGrid',
      refreshGrid: 'refreshMainGridWhenDrawerIsNotOpen',
      refreshGridOnCurrentPosition: 'refreshApp'
    },
    getDataUrl: '/submissionsView',
    uniqueField: 'submissionId',
    checkboxSelection: false
  };

  /**
   * @inheritDoc
   */
  additionalGridTitle = () => {
    const {
      pageTitle,
      pageTitleCount,
      filtersAsSearchBar,
      renderSearchBar
    } = this.props.searchBarConfig;
    let total = this.gridRef?.gridRef?.state.data?.length || 0;
    let { checkboxSelectionData, warningBannerMessage } = this.state;


    return (
      <>
        <HBox gap={2} alignItemsBaseLine>
          <h2 className={'tp-font-size-xl tp-text-semibold'}>{pageTitle}</h2>
          {!filtersAsSearchBar && isUndefined(renderSearchBar) &&
            <HBox>
              <span className={'pd-tasks-counter tp-font-size-sm'}>
                <span className={'pd-task-number'}>
                  {pageTitleCount(total)}
                </span>
              </span>
              {this.checkboxSelectionEnabled && <>
                <span className='tp-space-right-half-1 tp-space-left-half-1'>|</span>
                <span className={'pd-submission-selection tp-color-light-LM tp-font-size-sm'}>
                  {this.translate('submissionsCheckboxSelected', { count: checkboxSelectionData?.length })}
                </span>
              </>}
            </HBox>}
        </HBox>
        <div role={'alert'} className={'pd-notification-alert'}>
          <NotificationGroup>
            {warningBannerMessage && <Notification
              className={'tp-informational'}
              type={{ style: 'warning', icon: true }}
            >
              {warningBannerMessage}
            </Notification>}
          </NotificationGroup>
        </div>
      </>
    )
  }

  /**
   * Date cell renderer
   * @param cellProps
   */
  dateCellRenderer = (cellProps: GridCellProps) => {
    let dataItem = cellProps.dataItem;
    const { dueDate } = dataItem;
    let date;
    let field = cellProps.field;
    const colorNeeded = this.isStatusColorNeeded(dataItem);
    let className = cellProps.className;
    if (colorNeeded && dueDate && field === 'dueDate.date') {
      const { dueSoon } = this.state;
      const isExpired = isDateExpired(dueDate.date);
      const isDueSoon = isSubmissionDueSoon(dueDate.date, dueSoon);
      className = classNames(cellProps.className, {
        'tp-date-due-soon': isDueSoon,
        'tp-date-expired': isExpired
      });
    }
    if (field) {
      let properties = field.split('.');
      const dataItemElement = dataItem[properties[0]];
      if (dataItemElement && dataItemElement[properties[1]]) {
        date = new Date(dataItemElement[properties[1]]);
      } else {
        date = '';
      }
    }
    let loadingCell = this.loadingCell(cellProps);
    let cellValue = this.translateByNamespace('date.dateWithTime', { date: date });
    const tdProps = getTableTdAccessibilityProps(cellProps);
    return (
      loadingCell ? loadingCell : (
        <td {...tdProps} className={className}>
          {cellValue}
        </td>
      )
    );
  };

  /**
   * Cell renderer for description column
   */
  descriptionRenderer = (cellProps: GridCellProps) => {
    const tdProps = getTableTdAccessibilityProps(cellProps);
    return (
      <td {...tdProps} className={cellProps.className}>
        <WithTooltip
          context={this}
          title={cellProps.dataItem.description}
          htmlTag={'span'}
        >
          {cellProps.dataItem.description}
        </WithTooltip>
      </td>
    )
  };

  /**
   * Renderer for favorites column cells
   */
  favoriteCellRender = (cellProps: GridCellProps) => {
    let value: boolean = false;
    const { templatesOnly } = this.props;
    if (cellProps.field) {
      value = cellProps.dataItem[cellProps.field];
    }
    let loadingCell = this.loadingCell(cellProps);
    const tdProps = getTableTdAccessibilityProps(cellProps);
    return (
      loadingCell ? loadingCell :
        (
          <td
            className={`${cellProps.className} k-text-center pd-favorite-column-cell`}
            aria-label={templatesOnly ? this.translate('columnHeaders.favoriteTemplate') : this.translate('columnHeaders.favorite')}
            {...tdProps}
          >
            <PuzzleCheckbox
              className={`tp-checkbox-favorite ${value ? 'qa-fakeClass-checked' : ''}`}
              defaultChecked={value}
              aria-label={this.translate('SRL-favorTemplate')}
              titleText={this.translate('SRL-favorTemplate')}
              onChange={this.onFavoriteChange.bind(this, cellProps)}
              tooltipPosition={'right'}
              withTooltip
            />
          </td>
        )
    )
  };

  /**
   * Files count cell renderer
   */
  filesCountRender = (cellProps: GridCellProps) => {
    let loadingCell = this.loadingCell(cellProps);
    const tdProps = getTableTdAccessibilityProps(cellProps);
    return (
      loadingCell ? loadingCell : (
        <td
          {...tdProps}
          className={cellProps.className}
        >
          {cellProps.dataItem.fileCount}
        </td>
      )
    )
  };

  /**
   * Returns either start or deliver icon with appropriate action on it
   */
  getAppropriateAction = (cellProps: GridCellProps) => {
    const {
      className,
      dataItem = [{}],
      style
    } = cellProps;
    let children;
    const decodedTasks = disableActions([dataItem]);

    const { checkboxSelectionData } = this.state;
    const checkboxSelection = dataItem.checkboxSelection && checkboxSelectionData && checkboxSelectionData.length > 1;

    if (decodedTasks.includes("start submission")) {
      children = (
        <PuzzleButton
          aria-label={this.translate('SRL-startSubmission')}
          disabled={checkboxSelection}
          titleText={this.translate('SRL-startSubmission')}
          iconClass="tp-icon tp-play"
          fillMode="flat"
          themeColor={'primary'}
          onClick={() => this.dispatch('startSubmissionRequest', '',
            { submissionData: dataItem })}
          tooltipPosition={'left'}
          withTooltip
        />)

    } else if (decodedTasks.includes("download submission deliverables")) {
      children = (
        <PuzzleButton
          aria-label={this.translate('SRL-downloadDeliverables')}
          disabled={checkboxSelection}
          titleText={this.translate('SRL-downloadDeliverables')}
          iconClass="tp-icon tp-icon-md tp-download"
          fillMode="flat"
          themeColor={'primary'}
          onClick={() => this.dispatch('downloadAllDeliverables', '', {
            submissionId: dataItem.submissionId,
            params: {
              downloadAllDeliverables: true
            }
          })}
          tooltipPosition={'left'}
          withTooltip
        />
      )
    }
    const tdProps = getTableTdAccessibilityProps(cellProps);
    return (
      <td
        {...tdProps}
        aria-label={this.translate('columnHeaders.submissionAction')}
        className={`${className} pd-action-column-cell`}
        style={style}
      >
        {children}
      </td>
    );
  };

  getFolderParamForRequest = () => this.props.templatesOnly ? 'SUBMISSION_TEMPLATES' : 'DEFAULT_SUBMISSION';

  /**
   * @inheritDoc
   */
  isStatusColorNeeded = (dataItem: any) => {
    const { CANCELLED, COMPLETED, ON_HOLD } = SUBMISSION_STATUSES_MAP_SERVER_CORE_UI;
    const statusName = dataItem?.submissionStatus?.name;
    return !(CANCELLED.includes(statusName) || COMPLETED.includes(statusName) || (ON_HOLD.includes(statusName) && isDateExpired(dataItem.dueDate)));
  };

  /**
   * Action menu column renderer
   */
  menuActionColumnCellRenderer = (cellProps: GridCellProps) => {
    let menuActionCell = this.loadingCell(cellProps);
    if (!menuActionCell) {
      menuActionCell = this.props.templatesOnly ?
        this.renderTemplateActions(cellProps) :
        this.getAppropriateAction(cellProps);
    }
    return menuActionCell
  };

  /**
   * Drop event handler handler
   * @param e
   */
  onDropHandler = (e: React.DragEvent) => {
    e.preventDefault();
    const { files } = e.dataTransfer;
    this.setState({
      dragging: false
    }, () => {
      this.dispatch('openPreviewTemplatesDialog', '', {
        files
      });
    });
  };


  /**
   * Cell renderer for phaseStatus column
   */
  phaseStatusRenderer = (cellProps: GridCellProps) => {
    let field = cellProps.field;
    let value;
    if (field) {
      value = cellProps.dataItem[field].name;
    }

    let loadingCell = this.loadingCell(cellProps);
    const tdProps = getTableTdAccessibilityProps(cellProps);
    return (
      loadingCell ? loadingCell : (<td {...tdProps} className={cellProps.className}>{value}</td>)
    )
  };

  /**
   * Progress column cell renderer
   */
  progressCellRender = (cellProps: GridCellProps) => {
    let value;
    let completedProgress: number = 0;
    let progress: number = 0;
    let dataItem = cellProps?.dataItem;
    let errorBar = false;
    if (cellProps?.field) {
      value = dataItem[cellProps.field] || 0;

      let {
        numberOfAvailableFiles: availableCount,
        numberOfCompletedFiles: completedCount,
        numberOfDeliveredFiles: deliveredCount,
        numberOfInProcessFiles: inProcessCount,
        numberOfCanceledFiles: canceledCount
      } = value;
      let allFilesCount = availableCount + completedCount + deliveredCount + inProcessCount + canceledCount;
      errorBar = !!canceledCount;
      let completedStatuses = ['PROCESSED', 'COMPLETED'];
      let submissionStatus = dataItem.submissionStatus;
      let isCompleted = (allFilesCount === 0) && submissionStatus && (completedStatuses.indexOf(submissionStatus) !== -1);

      completedProgress = isCompleted ? 100 : Math.round(((completedCount + deliveredCount) / (allFilesCount - canceledCount)) * 100);
      completedProgress = completedProgress || 0; //NaN fix
      progress = isCompleted ? 0 : Math.round(((inProcessCount) / (allFilesCount - canceledCount)) * 100);
      progress = progress + completedProgress || 0; //NaN fix;
    }

    let loadingCell = this.loadingCell(cellProps);
    const tdProps = getTableTdAccessibilityProps(cellProps);
    return (
      loadingCell ? loadingCell : (
        <td {...tdProps} className={cellProps.className}>
          <GridProgressBar completedProgress={completedProgress} progress={progress} errorBar={errorBar} />
        </td>
      )
    )
  };

  /**
   * Project name renderer
   */
  projectNameRenderer = ((cellProps: GridCellProps) => {
    const { dataItem, field } = cellProps;
    let valueItem;
    if (field) {
      valueItem = dataItem[field];
    }

    let loadingCell = this.loadingCell(cellProps);
    const tdProps = getTableTdAccessibilityProps(cellProps);
    return (
      loadingCell ? loadingCell :
        (
          <td {...tdProps} className={cellProps.className}>
            {valueItem}
          </td>
        )
    );
  });

  /**
   * Renders buttons in template action column
   */
  renderTemplateActions = (cellProps: GridCellProps) => {
    const { dataItem = {}, className, style } = cellProps;
    const { tasks = [] } = dataItem;
    const tdProps = getTableTdAccessibilityProps(cellProps);
    let menuActionCell =
      (<td
        {...tdProps}
        className={`${className} pd-action-column-cell`}
        style={style}
        aria-label={this.translate('columnHeaders.templateAction')}
      >
        {tasks?.map((task: any) => {
          const isEdit = task.taskName === "manual edit template submission";
          const className = isEdit ? "tp-icon tp-edit" : "tp-icon tp-delete";
          const action = isEdit ? this.templateEdit.bind(this, dataItem) : this.templateDelete.bind(this, dataItem);
          return (
            <PuzzleButton
              aria-label={isEdit ? this.translate('SRL-iconEdit') : this.translate('SRL-iconDelete')}
              titleText={isEdit ? this.translate('SRL-iconEdit') : this.translate('SRL-iconDelete')}
              key={task.taskName}
              fillMode="flat"
              themeColor={'primary'}
              iconClass={className}
              onClick={action}
              tooltipPosition={'left'}
              withTooltip
            />
          )
        })}
      </td>);
    return menuActionCell;
  };

  /**
   * SubmissionStatus color cell render
   */
  statusColorCellRender = (cellProps: GridCellProps): ReactElement<HTMLTableCellElement> => {
    const dataItem = cellProps.dataItem;
    const { dueDate } = dataItem;
    let className = `${cellProps.className} pd-status-color-column-cell`;
    let title = '';
    const colorNeeded = this.isStatusColorNeeded(dataItem);
    if (colorNeeded && dueDate) {
      const { dueSoon } = this.state;
      const isExpired = isDateExpired(dueDate.date);
      const isDueSoon = isSubmissionDueSoon(dueDate.date, dueSoon);
      className = classNames(`pd-status-color-column-cell`, {
        'tp-status-warning': isDueSoon,
        'tp-status-expired': isExpired
      });
      title = this.generateDueDateTooltipTitle(isExpired, isDueSoon, dueDate.date);
    }

    let tdProps: any = getTableTdAccessibilityProps(cellProps);
    tdProps.className = className;
    return title.length ?
      <WithTooltip context={this} htmlTag='td' className={className} title={title} additionalProps={tdProps}>
        <div title={title} className='pd-status-color-column-indicator' />
      </WithTooltip> :
      <td {...tdProps} className={className}>
        <div className='pd-status-color-column-indicator' />
      </td>;
  };

  /**
   * Cell render for status column
   */
  statusRender = (cellProps: GridCellProps) => {
    let statusClass = '';
    let statusText = '';
    const { submissionStatus } = cellProps.dataItem;
    if (submissionStatus) {
      let statusName = submissionStatus.name;
      statusClass = classNames(`tp-main-grid-status-cell ${statusName.replace(' ', '-').toLowerCase()}`);
      statusText = commonLocalizationGetter(`SUBMISSION.statuses.${statusName}`);
    }

    let loadingCell = this.loadingCell(cellProps);
    const tdProps = getTableTdAccessibilityProps(cellProps);
    return (
      loadingCell ? loadingCell :
        (
          <td {...tdProps} className={cellProps.className}>
            <div className={statusClass}>
              {statusText}
            </div>
          </td>
        )
    );
  };

  /**
   * @inheritDoc
   */
  showDragAndDrop = () => {
    let {
      dragging,
      drawerOpen
    } = this.state;

    const hasApplyTemplatePolicy: boolean = Policies.hasApplyTemplatePolicy;
    const hasSubmissionCreateFromTemplatePolicy: boolean = Policies.hasSubmissionCreateFromTemplatePolicy;
    return dragging && hasApplyTemplatePolicy && hasSubmissionCreateFromTemplatePolicy && !drawerOpen;
  }

  /**
   * Cell renderer for submissionId column
   * @param cellProps
   */
  submissionIdRender = ((cellProps: GridCellProps): JSX.Element => {
    const { dataItem, field } = cellProps;
    let valueItem;


    if (field) {
      valueItem = dataItem[field];
    }
    let loadingCell = this.loadingCell(cellProps);
    const tdProps = getTableTdAccessibilityProps(cellProps);
    return (
      loadingCell ? loadingCell :
        (
          <td {...tdProps} className={cellProps.className}>
            {valueItem}
          </td>
        )
    );
  });

  /**
   * Cell renderer for submissionName column
   * Add tooltip
   */
  submissionNameRenderer = ((cellProps: GridCellProps) => {
    const { dataItem, field } = cellProps;
    let valueItem;
    if (field) {
      valueItem = dataItem[field];
    }

    let loadingCell = this.loadingCell(cellProps);
    const tdProps = getTableTdAccessibilityProps(cellProps);
    return (
      loadingCell ? loadingCell :
        (
          <td {...tdProps} className={cellProps.className}>
            {valueItem}
          </td>
        )
    );
  });

  /**
   * Words column cell renderer
   */
  wordsCellRender = (cellProps: GridCellProps) => {
    let loadingCell = this.loadingCell(cellProps);
    const tdProps = getTableTdAccessibilityProps(cellProps);
    const {
      className = ''
    } = cellProps
    return (
      loadingCell ? loadingCell : (
        <td className={`${className} tp-sortable pd-cell-with-small-h-padding`} {...tdProps}>
          <WordCountLink
            beforeAction={this.selectSubmission.bind(this, cellProps.dataItem)}
            data={cellProps.dataItem}
            onlyNumber={true}
            selectSubmission={true}
          />
        </td>
      )
    )
  };

  /**
   * Quote status cell renderer
   */
  quoteStatusRender = (cellProps: GridCellProps): JSX.Element => {
    const { quoteStatus } = cellProps.dataItem;
    let cellCls = `${cellProps.className} tp-quote-status`;
    let iconClass = "tp-icon tp-icon-sm";

    switch (quoteStatus) {
      case "UPDATED":
      case "PROVIDED":
        iconClass += " tp-check-underline-circle provided";
        cellCls += ' provided';
        break;
      case "REQUESTED":
        iconClass += " tp-arrow-up-right-circle requested";
        cellCls += ' requested';
        break;
      case "REQUEST_PENDING":
        iconClass += " tp-clock-filled pending";
        cellCls += ' pending';
        break;
      case "APPROVED":
        iconClass += " tp-check-circle-20 approved";
        cellCls += ' approved';
        break;
      case "REJECTED":
        iconClass += " tp-close-circle-20 rejected";
        cellCls += ' rejected';
        break;
      case "WITHDRAWN":
        iconClass += " tp-do-not-disturb-fill withdrawn";
        cellCls += ' withdrawn';
        break;
      case "COUNTERED":
        iconClass += " tp-arrow-left-circle-filled update-requested";
        cellCls += ' update-requested';
        break;
    }

    const tdProps = getTableTdAccessibilityProps(cellProps);

    return (
      <td
        className={cellCls}
        {...tdProps}
      >
        {quoteStatus !== 'PENDING' ? (
          <>
            <div className={'k-hbox k-align-items-center tp-pointer-events-none'}>
              <i className={iconClass} aria-hidden={"true"} />
              <span>{quoteStatus && commonLocalizationGetter(`QUOTES.statuses.${quoteStatus}`)}</span>
            </div>
          </>
        ) : ''}
      </td>
    )
  }

  /**
   * @inheritDoc
   */
  CELL_RENDER_MAP: any = {
    checkboxSelection: this.checkboxSelectionCellRenderer,
    customField: this.customFieldRender.bind(this),
    dateCompleted: this.dateCellRenderer,
    dateCreated: this.dateCellRenderer,
    dateStarted: this.dateCellRenderer,
    description: this.descriptionRenderer,
    dueDate: this.dateCellRenderer,
    fileCount: this.filesCountRender,
    fileProgress: this.progressCellRender,
    instructions: this.renderNotesOrInstructionIcon.bind(this, false),
    isFavorite: this.favoriteCellRender,
    notes: this.renderNotesOrInstructionIcon.bind(this, true),
    menuAction: this.menuActionColumnCellRenderer.bind(this),
    owner: this.ownerAndAssignToCellRender,
    phaseStatus: this.phaseStatusRenderer,
    priority: this.priorityRenderer,
    projectName: this.projectNameRenderer,
    quoteStatus: this.quoteStatusRender,
    submissionName: this.submissionNameRenderer,
    status: this.statusRender.bind(this),
    statusColor: this.statusColorCellRender.bind(this),
    submissionId: this.submissionIdRender,
    wordCount: this.wordsCellRender
  };

  /**
   * @inheritDoc
   */
  GET_DATAINDEX_MAP: any = {
    pastDueDateComboBox: 'dueDate',
    priorityList: 'priority',
    projectNameInputText: "projectName",
    quoteStatuses: "quoteStatus",
    submissionId: "submissionId",
    submissionNameInputText: "submissionName",
    submissionOwnerValues: 'owner',
    submissionStatusValues: "status"
  };
  /**
   * @inheritDoc
   */
  GET_FILTERNAME_MAP = {
    dueDate: 'pastDueDateComboBox',
    owner: 'submissionOwnerValues',
    priority: 'priority',
    projectName: "projectNameInputText",
    quoteStatus: "quoteStatuses",
    status: "submissionStatusValues",
    submissionId: "submissionId",
    submissionName: "submissionNameInputText"
  };
}

export default withDynamicSize(gridFilter(MainGrid));
